import React, { useCallback, useEffect, useMemo, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { workspacesApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { WORKSPACE_ROLES } from "../../utils/permissions";
import AccessDialog from "./AccessDialog";
import InviteDialog from "./InviteDialog";
import { Caution, ConfirmDialog, EmptyState, ListSkeleton, Refusal, Surface, menuPaper, quietDanger, rowSx } from "./WorkspaceUi";
import { formatDate, fullName, initials, opensText, seatsFull, seatText } from "./workspaceFormat";

const roleLabel = (key) => WORKSPACE_ROLES.find((role) => role.key === key)?.label || "Member";

// Why a membership was archived, in words, with when.
const ARCHIVE_REASONS = { removed: "archived", left: "left", "keep-only": "moved to another workspace" };
const archiveReason = (entry) => {
  const what = ARCHIVE_REASONS[entry.reason] || "archived";
  const when = formatDate(entry.archivedAt);
  return when ? `${what} ${when}` : what;
};

// People in the workspace: role inline, the views they hold, invitations,
// archiving. A view carries the one database it is for, so the views beside
// a name are exactly the databases that person reaches - given on the Views
// tab, where "Who sees this" is.
export default function UsersTab({ workspace, user, mayChange, isStaff, notify, onChanged }) {
  const [data, setData] = useState(null);
  // The workspace's view library: what a person can be given.
  const [library, setLibrary] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [inviting, setInviting] = useState(false);
  const [menu, setMenu] = useState({ anchor: null, person: null });
  const [confirm, setConfirm] = useState(null);
  const [changing, setChanging] = useState(null);
  // "What this person sees": read-only, open to anyone who can read the tab.
  const [seeing, setSeeing] = useState(null);
  // Memberships that were archived here, for bpExperts to bring back.
  const [archived, setArchived] = useState([]);

  const load = useCallback(async () => {
    const [people, viewList, past] = await Promise.all([
      workspacesApi.users(workspace.id),
      workspacesApi.views(workspace.id),
      isStaff ? workspacesApi.archivedMembers(workspace.id) : Promise.resolve(null),
    ]);

    if (people.success) {
      setData(people.content);
      setError("");
    } else setError(people.msg || "Couldn't load the people in this workspace.");
    setLibrary(viewList.success ? viewList.content || [] : []);
    setArchived(past?.success ? past.content || [] : []);
  }, [workspace.id, isStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const people = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.users || []).filter(
      (person) => !needle || [person.email, person.firstName, person.lastName].filter(Boolean).some((v) => v.toLowerCase().includes(needle))
    );
  }, [data, query]);

  // The workspace's active views - what a person can be given.
  const activeViews = library.filter((view) => !view.archivedAt && !view.builtIn);
  const viewName = (id) => library.find((view) => view.id === id)?.name;

  // Runs one change; on success says what happened and reloads. A refusal is
  // returned to the caller (a dialog keeps it) or shown above the list.
  const act = async (request, done, { showError = true } = {}) => {
    const res = await request();
    if (res.success) {
      notify(typeof done === "function" ? done(res) : done || res.msg);
      await load();
      onChanged();
    } else if (showError) setError(res.msg || "That didn't work. Try again.");
    return res;
  };

  const setRole = async (person, role) => {
    setChanging(person.id);
    const res = await act(() => workspacesApi.setRole(workspace.id, person.id, role), `${fullName(person)} is now ${role === "admin" ? "an admin" : "a member"}.`, {
      showError: false,
    });
    setChanging(null);
    return res;
  };

  const changeRole = async (person, role) => {
    if (role === person.role) return;
    // Stepping down yourself takes away this screen: ask first.
    if (person.id === user?.id && role !== "admin") {
      setConfirm({
        title: "Make yourself a member?",
        body: `You'll no longer manage ${workspace.name}: inviting people, roles and views need another admin afterwards.`,
        confirmLabel: "Make me a member",
        tone: "primary",
        run: () => setRole(person, role),
      });
      return;
    }
    const res = await setRole(person, role);
    if (!res.success) setError(res.msg || "Couldn't change the role.");
  };

  if (!data && !error) return <ListSkeleton rows={5} />;

  const full = seatsFull(data?.seats);
  const fullNext = isStaff
    ? "Archive someone, withdraw an invitation, or raise the seats on the Plan tab."
    : "Archive someone, withdraw an invitation, or ask bpExperts for more seats.";
  const fullHint = `All seats are in use. ${fullNext}`;
  const inviteButton = (label = "Invite") =>
    mayChange && (
      <Tooltip title={full ? fullHint : ""}>
        <span>
          <Button variant="contained" startIcon={<PersonAddAlt1RoundedIcon />} disabled={full} onClick={() => setInviting(true)}>
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  const noOneYet = (data?.users || []).length === 0;

  return (
    <Stack spacing={2.5}>
      {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}

      {data && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            placeholder="Search people"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search people" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 320 }, "& .MuiOutlinedInput-root": { bgcolor: "#fff" } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 22rem", minWidth: "16rem" }}>
            {seatText(data.seats)}
          </Typography>
          {!noOneYet && inviteButton()}
        </Stack>
      )}

      {mayChange && full && <Caution title="All seats are in use">{fullNext}</Caution>}

      {data && (
        <Surface>
          {people.length === 0 ? (
            query ? (
              <EmptyState icon={<SearchOffRoundedIcon />} title="No one matches that search">
                Try part of a name or email address.
              </EmptyState>
            ) : (
              <EmptyState icon={<GroupAddRoundedIcon />} title={mayChange ? "Invite your first colleague" : "No one here yet"} action={inviteButton("Invite a colleague")}>
                {mayChange
                  ? "Invite people by email as Admin or Member. They get a link that works once, for 14 days."
                  : "When the workspace's admins invite people, they show here."}
              </EmptyState>
            )
          ) : (
            people.map((person, index) => {
              const self = person.id === user?.id;
              // Archiving yourself is leaving: that has its own button. bpExperts may still do it here.
              const mayManage = mayChange && (!self || isStaff);
              return (
                <Stack
                  key={person.id}
                  direction={{ xs: "column", md: "row" }}
                  spacing={{ xs: 1.25, md: 2 }}
                  alignItems={{ md: "center" }}
                  sx={{ ...rowSx(!index), "&:hover": { bgcolor: brand.shell } }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1.3, minWidth: 0 }}>
                    <Avatar sx={{ width: 38, height: 38, bgcolor: brand.ink, color: brand.signal, fontSize: "0.85rem", fontWeight: 700 }}>
                      {initials(person)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{fullName(person)}</Typography>
                        {self && <Chip size="small" label="You" sx={{ height: 20, bgcolor: brand.signal, color: brand.ink, fontWeight: 600 }} />}
                        {person.otherWorkspaces.length > 0 && (
                          <Tooltip title={`Also in ${person.otherWorkspaces.map((w) => w.name).join(", ")}`}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Belongs to ${person.otherWorkspaces.length + 1} workspaces`}
                              sx={{ height: 20, color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        {person.email}
                        {person.lastSignInAt ? ` · last signed in ${formatDate(person.lastSignInAt)}` : ""}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* The views they hold, and - because a member reaches a
                      database only through one, while an admin reaches every
                      database of the workspace by role - which databases that
                      actually leaves them able to open. */}
                  <Box sx={{ flex: 1, minWidth: 0, pl: { xs: 6.5, md: 0 } }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(person.views || []).map((view) => (
                        <Chip
                          key={view.id}
                          size="small"
                          label={view.database ? `${view.name} · ${view.database}` : `${view.name} · no database yet`}
                          sx={{ bgcolor: brand.canvas, border: `1px solid ${brand.line}` }}
                        />
                      ))}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: (person.views || []).length ? 0.5 : 0, overflowWrap: "anywhere" }}
                    >
                      {opensText(person, workspace.databases || [], activeViews.length > 0)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: { xs: 6.5, md: 0 } }}>
                    {mayChange ? (
                      <TextField
                        select
                        size="small"
                        value={person.role}
                        disabled={changing === person.id}
                        onChange={(e) => changeRole(person, e.target.value)}
                        SelectProps={{ MenuProps: menuPaper }}
                        sx={{ width: 130, "& .MuiOutlinedInput-root": { bgcolor: "#fff" } }}
                        inputProps={{ "aria-label": `Role of ${fullName(person)}` }}
                      >
                        {WORKSPACE_ROLES.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Chip
                        size="small"
                        label={roleLabel(person.role)}
                        sx={
                          person.role === "admin"
                            ? { bgcolor: brand.ink, color: "#fff", fontWeight: 600 }
                            : { bgcolor: brand.canvas, border: `1px solid ${brand.line}`, fontWeight: 600 }
                        }
                      />
                    )}
                    <IconButton aria-label={`More for ${fullName(person)}`} onClick={(e) => setMenu({ anchor: e.currentTarget, person, mayManage })}>
                      <MoreVertRoundedIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              );
            })
          )}
        </Surface>
      )}

      {(data?.invitations || []).length > 0 && (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Pending invitations{" "}
            <Typography component="span" color="text.secondary" sx={{ fontWeight: 500 }}>
              ({data.invitations.length})
            </Typography>
          </Typography>
          <Surface>
            {data.invitations.map((invitation, index) => {
              // An invitation carries the views the person is given on
              // accepting; an older one may still name groups instead.
              const names = [...(invitation.viewIds || []), ...(invitation.groupIds || [])].map(viewName).filter(Boolean);
              return (
                <Stack
                  key={invitation.id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  sx={rowSx(!index)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: brand.canvas, color: brand.ink, border: `1px dashed ${brand.line}` }}>
                      <MailOutlineRoundedIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{invitation.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${roleLabel(invitation.role === "admin" ? "admin" : "member")}${names.length ? ` · ${names.join(", ")}` : ""}${
                          invitation.expiresAt ? ` · expires ${formatDate(invitation.expiresAt)}` : ""
                        }`}
                      </Typography>
                    </Box>
                  </Stack>
                  {mayChange && (
                    <Stack direction="row" spacing={1} sx={{ pl: { xs: 5.75, sm: 0 } }}>
                      <Button
                        size="small"
                        startIcon={<SendRoundedIcon />}
                        onClick={() => act(() => workspacesApi.resendInvite(workspace.id, invitation.id), `Invitation sent again to ${invitation.email}.`)}
                      >
                        Resend
                      </Button>
                      <Button
                        size="small"
                        startIcon={<UndoRoundedIcon />}
                        sx={quietDanger}
                        onClick={() =>
                          setConfirm({
                            title: `Withdraw the invitation to ${invitation.email}?`,
                            body: "The link stops working and the seat is free again. You can invite them again later.",
                            confirmLabel: "Withdraw",
                            run: () => act(() => workspacesApi.revokeInvite(workspace.id, invitation.id), `Invitation to ${invitation.email} withdrawn.`, { showError: false }),
                          })
                        }
                      >
                        Withdraw
                      </Button>
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Surface>
        </Box>
      )}

      {isStaff && archived.length > 0 && (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            Archived people{" "}
            <Typography component="span" color="text.secondary" sx={{ fontWeight: 500 }}>
              ({archived.length})
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nothing is deleted. Restoring brings back the role and the views they held, if a seat is free and they belong to no other workspace.
          </Typography>
          <Surface>
            {archived.map((entry, index) => (
              <Stack
                key={entry.entryId}
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
                sx={rowSx(!index)}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: brand.canvas, color: brand.ink, border: `1px dashed ${brand.line}`, fontSize: "0.75rem", fontWeight: 700 }}>
                    {initials(entry)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{fullName(entry)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {`${entry.email} · ${archiveReason(entry)}${entry.archivedBy ? ` by ${entry.archivedBy}` : ""} · was ${roleLabel(entry.role)}`}
                    </Typography>
                  </Box>
                </Stack>
                {mayChange && (
                  <Button
                    size="small"
                    startIcon={<RestoreRoundedIcon />}
                    aria-label={`Restore ${fullName(entry)}`}
                    sx={{ pl: { xs: 5.75, sm: 0 } }}
                    onClick={() =>
                      setConfirm({
                        title: `Restore ${fullName(entry)} to ${workspace.name}?`,
                        body: `They come back as ${roleLabel(entry.role)}, holding the views they had that still exist, and so reaching those databases again.`,
                        confirmLabel: "Restore",
                        tone: "primary",
                        run: () => act(() => workspacesApi.restoreMember(workspace.id, entry.entryId), null, { showError: false }),
                      })
                    }
                  >
                    Restore
                  </Button>
                )}
              </Stack>
            ))}
          </Surface>
        </Box>
      )}

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={() => setMenu({ anchor: null, person: null })} {...menuPaper}>
        <MenuItem
          onClick={() => {
            const person = menu.person;
            setMenu({ anchor: null, person: null });
            setSeeing(person);
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          What this person sees…
        </MenuItem>
        {menu.mayManage && isStaff && menu.person?.otherWorkspaces.length > 0 && (
          <MenuItem
            onClick={() => {
              const person = menu.person;
              setMenu({ anchor: null, person: null });
              setConfirm({
                title: `Keep ${fullName(person)} only in ${workspace.name}?`,
                body: `Their memberships of ${person.otherWorkspaces.map((w) => w.name).join(", ")} are archived. Each can be restored.`,
                confirmLabel: "Keep only this workspace",
                tone: "primary",
                run: () => act(() => workspacesApi.keepOnly(workspace.id, person.id), null, { showError: false }),
              });
            }}
          >
            <ListItemIcon>
              <CallSplitRoundedIcon fontSize="small" />
            </ListItemIcon>
            Keep only this workspace…
          </MenuItem>
        )}
        {menu.mayManage && (
        <MenuItem
          sx={{ color: notice.fix.fg }}
          onClick={() => {
            const person = menu.person;
            const self = person.id === user?.id;
            setMenu({ anchor: null, person: null });
            setConfirm({
              title: self ? `Leave ${workspace.name}?` : `Archive ${fullName(person)} from ${workspace.name}?`,
              body: self
                ? "You lose access to this workspace's databases at once. Your account stays."
                : "They lose access to this workspace's databases at once. Their account stays, and bpExperts can restore the membership.",
              confirmLabel: self ? "Leave" : "Archive",
              run: () => act(() => workspacesApi.archiveUser(workspace.id, person.id), `${fullName(person)} archived from ${workspace.name}.`, { showError: false }),
            });
          }}
        >
          <ListItemIcon>
            <ArchiveRoundedIcon fontSize="small" sx={{ color: notice.fix.fg }} />
          </ListItemIcon>
          Archive from this workspace…
        </MenuItem>
        )}
      </Menu>

      <AccessDialog open={Boolean(seeing)} workspace={workspace} person={seeing} onClose={() => setSeeing(null)} />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone || "error"}
        onConfirm={() => confirm.run()}
        onClose={() => setConfirm(null)}
      />

      <InviteDialog
        open={inviting}
        workspace={workspace}
        views={activeViews}
        seats={data?.seats}
        isStaff={isStaff}
        onClose={() => setInviting(false)}
        onInvited={async (msg) => {
          setInviting(false);
          notify(msg);
          await load();
          onChanged();
        }}
      />
    </Stack>
  );
}
